import {useState, useEffect} from 'react';
import {useErrorLogger} from '@shopify/react-bugsnag';
import {usePerformanceMark, Stage} from '@shopify/react-performance';
import ShopAndProductQueryString from '../graphql/queries/ShopAndProductQueryString';
import {PageContextProvider} from '../contexts/PageContext.client';
import Layout from './Layout.client';
import CartProvider from './CartProvider.client';
import Profile from './Profile.client';
import fetchQuery from '../graphql/fetchQuery';

import BasicLink from './Links/BasicLink.client';
import ShoppableLink from './Links/ShoppableLink.client';
import LoadingLink from './Links/LoadingLink.client';
import {
  linkIsPublicPageQueryResultLinksDovetaleAffiliateLink,
  linkIsPublicPageQueryResultLinksExternalLink,
  linkIsPublicPageQueryResultLinksShoppableLink,
  linkIsPublicPageQueryResultLinksAmazonAffiliateLink,
  PublicPageQueryResult,
  PublicPageQueryResultLinksExternalLink,
  PublicPageQueryResultLinksLink,
  PublicPageQueryResultLinksShoppableLink,
} from '../graphql/queries/LinkpopQuery';
import {isSupportedMediaLink, MediaLinkCard} from './Links/MediaLinkCard';
import classNames from 'classnames';
import {DovetaleAffiliateLink} from './Links/DovetaleAffiliateLink.client';
import {DovetaleAffiliateLinkContextProvider} from 'contexts/DovetaleAffiliateLinkContext.client';
import {AmazonAffiliateLink} from './Links/AmazonAffiliateLink.client';
import {useFlags} from 'shared/FeatureFlags/hooks';
import {base64Decode, sanitizeProduct} from 'shared/utils/base64';

export function filterInactiveProducts(
  links: (
    | PublicPageQueryResultLinksLink
    | PublicPageQueryResultLinksShoppableLink
    | PublicPageQueryResultLinksExternalLink
  )[],
  products?: {id: string}[],
) {
  return links.filter((link) => {
    if (!linkIsPublicPageQueryResultLinksShoppableLink(link)) {
      return true;
    }

    return products?.some(
      (product) => base64Decode(product?.id) === base64Decode(link.productId),
    );
  });
}

export default function PageDetails({
  LinkpopData,
}: {
  LinkpopData: {data: PublicPageQueryResult};
}) {
  const logger = useErrorLogger();
  const flags = useFlags();
  usePerformanceMark(Stage.Complete, 'PublicPage-hydrogen');
  // Initial data setup
  const storefrontAccessToken =
    LinkpopData?.data?.currentPage?.storefrontAccessToken;
  const storeUrl = LinkpopData?.data?.currentPage?.storeUrl;
  const links = LinkpopData?.data?.currentPage?.links.slice().reverse();

  // Parse out product Ids from Linkpop links
  const productIds = links
    .filter(linkIsPublicPageQueryResultLinksShoppableLink)
    .map((link) => link.productId)
    .map(base64Decode);

  const [hasLoadedProductData, setHasLoadedProductData] = useState(false);
  const [productLinks, setProductLinks] = useState<any[]>([]);
  const [countryCode, setCountryCode] = useState<string | undefined>();

  const getShopAndProductData = async () => {
    if (!storeUrl) {
      return null;
    }

    const correlationId = Math.random().toString(36).slice(2);

    const response = await fetchQuery({
      query: ShopAndProductQueryString({ids: productIds}),
      url: `${storeUrl}/api/2022-07/graphql.json?correlation-id=${correlationId}`,
      headers: {
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
      },
      onError: (error) => {
        logger.leaveBreadcrumb(`could not fetch store data for ${storeUrl}`);
        logger.leaveBreadcrumb(`Correlation ID: ${correlationId}`);
        logger.notify(error);
      },
    });

    return response;
  };

  useEffect(() => {
    getShopAndProductData().then((productData) => {
      setHasLoadedProductData(true);

      setCountryCode(
        productData?.data?.shop?.paymentSettings?.countryCode ?? undefined,
      );

      const products = productData?.data?.nodes.map(sanitizeProduct);
      // TODO: Remove this and use the `removeLinksForInactiveProducts` fn
      //       from `web`, when typings can be shared between web+hydrogen.
      const activeLinks = filterInactiveProducts(links, products);

      const linksWithProductInfo = activeLinks.map((link) => {
        if (linkIsPublicPageQueryResultLinksShoppableLink(link)) {
          return {
            ...link,
            productInfo: products.find((p: unknown) => {
              if (p) {
                // @ts-expect-error TODO: type definition for `product`
                return base64Decode(p.id) === base64Decode(link.productId);
              }
              return false;
            }),
          };
        } else {
          return link;
        }
      });
      setProductLinks(linksWithProductInfo);
    });
  }, []);

  const {title, bio, media, socialMediaAccounts, themeSettings} =
    LinkpopData.data?.currentPage || {};

  return (
    <PageContextProvider
      value={{
        id: LinkpopData.data.currentPage.id,
        slug: LinkpopData.data.currentPage.slug,
        productLinks,
      }}
    >
      <CartProvider countryCode={countryCode}>
        <DovetaleAffiliateLinkContextProvider links={links}>
          <Layout themeSettings={themeSettings}>
            <Profile
              title={title}
              bio={bio}
              profilePictureUrl={media?.url}
              socialMediaAccounts={socialMediaAccounts}
              themeSettings={themeSettings}
            />
            <div className="mt-6" />
            <div
              className={classNames(
                'flex',
                'flex-col',
                'content-center',
                'items-center',
                'space-y-4',
              )}
            >
              {links.map((link) => {
                if (linkIsPublicPageQueryResultLinksShoppableLink(link)) {
                  if (!hasLoadedProductData) {
                    return <LoadingLink key={link.id} />;
                  }

                  const linkWithProduct = productLinks.find(
                    (e) => e.id === link.id,
                  );

                  if (!linkWithProduct) {
                    return null;
                  }

                  return (
                    <ShoppableLink
                      style={themeSettings}
                      key={link.id}
                      id={link.id}
                      title={link.title}
                      product={linkWithProduct.productInfo}
                    />
                  );
                } else if (linkIsPublicPageQueryResultLinksExternalLink(link)) {
                  return (
                    <BasicLink
                      style={themeSettings}
                      key={link.id}
                      id={link.id}
                      title={link.title}
                      img={link?.media?.url}
                      url={link.url}
                    />
                  );
                } else if (isSupportedMediaLink(link)) {
                  return (
                    <MediaLinkCard
                      style={themeSettings}
                      key={link.id}
                      link={link}
                    />
                  );
                } else if (
                  linkIsPublicPageQueryResultLinksDovetaleAffiliateLink(link)
                ) {
                  if (!flags.CrypticCollab) {
                    return null;
                  }

                  return (
                    <DovetaleAffiliateLink
                      style={themeSettings}
                      key={link.id}
                      id={link.productId}
                      title={link.title}
                    />
                  );
                } else if (
                  linkIsPublicPageQueryResultLinksAmazonAffiliateLink(link)
                ) {
                  return (
                    <AmazonAffiliateLink
                      key={link.id}
                      {...link}
                      description={link.description}
                      style={themeSettings}
                    />
                  );
                }
              })}
            </div>
          </Layout>
        </DovetaleAffiliateLinkContextProvider>
      </CartProvider>
    </PageContextProvider>
  );
}
